import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, useSubscription } from '@apollo/client';
import { getEffectiveErpResourceId } from 'lib/selectors/getEffectiveErpResourceId';
import {
  SUBSCRIBE_OPEN_LABOR_TICKETS_FOR_CONTROL_BAR,
  GET_PROGRAM_EVENTS_GROUPED,
} from 'lib/api/queries';
import { getMachine } from 'lib/selectors/getMachine';
import { getSevenDayLookbackStart } from 'lib/selectors/getSevenDayLookbackStart';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { getIsSetupModeActive } from 'lib/selectors/getIsSetupModeActive';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import {
  actionSetCurrentWorkOrderOp,
  actionSetLaborTickets,
  actionSetEventsLoading,
  actionSetJobCardCount,
  actionSetHasCards,
  actionSetHasUnmatchedCards,
  actionSetHasOpenLaborTickets,
  actionSetOpenLaborTicketRefs,
  actionSetHasOpenSetupLaborTickets,
  actionSetProgramEvents,
} from 'lib/actions';

const POLL_INTERVAL_MS = 10_000;

/**
 * Fetches program events (via Apollo REST) and open labor tickets (GraphQL subscription)
 * so the ERPMultiJobControlBar knows whether to show "Submit All" or
 * "Select Jobs" — even when the apm-opdash-display MFE is not mounted
 * (e.g. the user is on a different tab after a page refresh).
 *
 * Also dispatches currentJob info to Redux so the header and other selectors
 * work correctly regardless of which tab is active.
 *
 * Returns { refetch, events, eventsLoading }.
 */
export const useProgramEventDisplayState = () => {
  const dispatch = useDispatch();
  const enabled = useSelector(getIsApmERPJobFlow);
  const isSetupModeActive = useSelector(getIsSetupModeActive);
  const machine = useSelector(getMachine);
  const lookbackStart = useSelector(getSevenDayLookbackStart);
  const currentOperator = useSelector(getCurrentOperator);
  const operatorErpId = currentOperator?.erpId ?? null;
  const machineRef = machine?.machineRef;
  const erpResourceId = useSelector(getEffectiveErpResourceId);

  const skip = !enabled || !machineRef || !lookbackStart;

  // ---- Program events (Apollo REST poll) ----
  const {
    data: eventsData,
    loading: eventsLoading,
    refetch: refetchEvents,
  } = useQuery(GET_PROGRAM_EVENTS_GROUPED, {
    variables: { machineRef, start: lookbackStart },
    skip,
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const events = useMemo(() => {
    if (skip) return [];
    return eventsData?.programEventsGrouped || [];
  }, [eventsData, skip]);

  // Apollo sets loading=true on every poll/refetch (network-only + notifyOnNetworkStatusChange).
  // Only treat the very first fetch as "loading"; once we've received any response (even []),
  // subsequent polls should not re-trigger loading (avoids flicker between poll cycles).
  const hasReceivedEvents = useRef(false);
  if (!eventsLoading && !skip) {
    hasReceivedEvents.current = true;
  }
  if (skip) {
    hasReceivedEvents.current = false;
  }
  const showEventsLoading = eventsLoading && !hasReceivedEvents.current;

  // Dispatch loading state to Redux for components that check it.
  useEffect(() => {
    dispatch(actionSetEventsLoading(showEventsLoading));
  }, [showEventsLoading, dispatch]);

  // Re-fetch when OperatorView signals a refresh (e.g. after job selection).
  const refetchRef = useRef(refetchEvents);
  refetchRef.current = refetchEvents;
  useEffect(() => {
    if (skip) return undefined;
    const handler = () => {
      return refetchRef.current();
    };
    window.addEventListener('apm:opdash-refresh', handler);
    return () => {
      return window.removeEventListener('apm:opdash-refresh', handler);
    };
  }, [skip]);

  // ---- Open labor tickets (GraphQL subscription) ----
  const ltWhere = useMemo(() => {
    return {
      _and: [
        { machineRef: { _eq: machineRef } },
        { clockOut: { _is_null: true } },
      ],
    };
  }, [machineRef]);

  const { data: ltData, loading: ltLoading } = useSubscription(
    SUBSCRIBE_OPEN_LABOR_TICKETS_FOR_CONTROL_BAR,
    {
      variables: { where: ltWhere },
      skip: !enabled || !machineRef,
    }
  );

  const openLaborTickets = useMemo(() => {
    return ltData?.erpLaborTickets ?? [];
  }, [ltData]);

  // ---- Derived state ----
  const {
    hasCards,
    hasUnmatchedCards,
    hasOpenLaborTickets,
    openLaborTicketRefs,
    hasOpenSetupLaborTickets,
    currentJob,
    cardCount,
  } = useMemo(() => {
    const openLtRefs = new Set(
      openLaborTickets.map((lt) => {
        return lt.laborTicketRef;
      })
    );

    // Every group the server sends is a card: the grouped endpoint trims
    // closed labor tickets' time out of intervals and drops fully-resolved
    // groups, so the old "exclude confirmed events whose LT is no longer
    // open" heuristic undercounted groups that still carry unresolved
    // intervals alongside a closed ticket.
    const visibleEvents = events;

    const visibleEventWooRefs = new Set(
      visibleEvents
        .filter((e) => {
          return e.workOrderOperationRef != null;
        })
        .map((e) => {
          return Number(e.workOrderOperationRef);
        })
    );

    // Standalone open LTs not covered by an event
    const standaloneLts = openLaborTickets.filter((lt) => {
      return (
        lt.workOrderOperation?.workOrderOperationRef != null &&
        !visibleEventWooRefs.has(lt.workOrderOperation.workOrderOperationRef)
      );
    });

    const matchedEvents = visibleEvents.filter((e) => {
      return e.workOrderOperationRef != null;
    });

    const setupOpenLts = openLaborTickets.filter((lt) => {
      return lt.type === 'setup';
    });

    const mySetupOpenLts = operatorErpId
      ? setupOpenLts.filter((lt) => {
          return String(lt.personId) === String(operatorErpId);
        })
      : [];

    // In setup mode the server also returns the interrupted production job as a
    // paused card, so it counts alongside the open setup tickets — otherwise
    // OperatorView would show its "Waiting for Work" fallback over a board that
    // has cards.
    const pausedEvents = visibleEvents.filter((e) => {
      return e.paused;
    });
    const cards = isSetupModeActive
      ? setupOpenLts.length > 0 || pausedEvents.length > 0
      : matchedEvents.length > 0 || standaloneLts.length > 0;
    const unmatchedCards = visibleEvents.some((e) => {
      return e.workOrderOperationRef == null;
    });
    const count = isSetupModeActive
      ? setupOpenLts.length + pausedEvents.length
      : matchedEvents.length + standaloneLts.length;

    // Header represents the active job: the run with a CURRENT OPEN ACTIVITY,
    // surfaced by the grouped endpoint as `hasOpenActivitySet` (an activity_set
    // with no closed_at for the WOO). Activities are kept aligned with program
    // events, so this is the authoritative "running right now" signal. A
    // completed run's activity set is closed → nothing active. (Keying off
    // closedDate / "most recent" wrongly kept a finished run active; the
    // program interval's end always extends to ~now, so it can't tell running
    // from just-completed.)
    const activeEvent = isSetupModeActive
      ? null
      : visibleEvents.find((e) => {
          return e.hasOpenActivitySet === true;
        }) ?? null;

    let job = null;
    if (isSetupModeActive) {
      job = null;
    } else if (activeEvent) {
      if (activeEvent.workOrderOperationRef != null) {
        const partOp = activeEvent.candidatePartOps?.[0];
        job = {
          workOrderOperationRef: activeEvent.workOrderOperationRef,
          workOrderId: activeEvent.workOrderId ?? null,
          partNumber: activeEvent.partNumber ?? partOp?.partNumber ?? null,
          sequenceNumber:
            activeEvent.sequenceNumber ?? partOp?.sequenceNumber ?? null,
          type: 'production',
          clockIn: activeEvent.eventTime,
          displayState:
            activeEvent.confirmedAt || activeEvent.everConfirmed
              ? 'CONFIRMED'
              : 'SUGGESTED',
        };
      } else if (standaloneLts.length > 0) {
        const lt = standaloneLts[0];
        job = {
          workOrderOperationRef:
            lt.workOrderOperation?.workOrderOperationRef ?? null,
          workOrderId: lt.workOrderId,
          partNumber: lt.workOrderOperation?.workOrder?.partNumber ?? null,
          sequenceNumber: lt.sequenceNumber,
          type: lt.type || 'production',
          clockIn: lt.clockIn,
          displayState: 'WAITING_FOR_PROGRAM',
        };
      }
    } else if (standaloneLts.length > 0) {
      const lt = standaloneLts[0];
      job = {
        workOrderOperationRef:
          lt.workOrderOperation?.workOrderOperationRef ?? null,
        workOrderId: lt.workOrderId,
        partNumber: lt.workOrderOperation?.workOrder?.partNumber ?? null,
        sequenceNumber: lt.sequenceNumber,
        type: lt.type || 'production',
        clockIn: lt.clockIn,
        displayState: 'WAITING_FOR_PROGRAM',
      };
    }

    return {
      hasCards: cards,
      hasUnmatchedCards: unmatchedCards,
      hasOpenLaborTickets: openLaborTickets.length > 0,
      openLaborTicketRefs: [...openLtRefs],
      hasOpenSetupLaborTickets: mySetupOpenLts.length > 0,
      currentJob: job,
      cardCount: count,
    };
  }, [events, openLaborTickets, isSetupModeActive, operatorErpId]);

  // ---- Redux dispatches (only when values actually change) ----
  const showLtLoading = ltLoading && openLaborTickets.length === 0;
  const loading = showEventsLoading || showLtLoading;
  const prevJobKey = useRef('');

  useEffect(() => {
    if (!enabled) return;
    if (loading) return;

    const key = JSON.stringify(currentJob);
    if (key === prevJobKey.current) return;
    prevJobKey.current = key;

    if (currentJob) {
      dispatch(
        actionSetCurrentWorkOrderOp({
          workOrderOperationRef: currentJob.workOrderOperationRef,
          workOrderId: currentJob.workOrderId,
          sequenceNumber: currentJob.sequenceNumber,
          workOrder: { partNumber: currentJob.partNumber },
        })
      );
      // Set a synthetic labor ticket so getJobStatus / getHasOpenJob work
      dispatch(
        actionSetLaborTickets([
          {
            laborTicketRef: null,
            state: 'OPEN',
            type: currentJob.type || 'production',
            clockIn: currentJob.clockIn,
            clockOut: null,
            workOrderId: currentJob.workOrderId,
            sequenceNumber: currentJob.sequenceNumber,
            resourceId: erpResourceId,
            workOrderOperation: {
              workOrderOperationRef: currentJob.workOrderOperationRef,
            },
          },
        ])
      );
    } else {
      dispatch(actionSetCurrentWorkOrderOp(null));
      dispatch(actionSetLaborTickets([]));
    }
  }, [enabled, currentJob, loading, dispatch, erpResourceId]);

  useEffect(() => {
    if (!enabled) return;
    if (!loading) {
      dispatch(actionSetJobCardCount(cardCount));
      dispatch(actionSetHasCards(hasCards));
      dispatch(actionSetHasUnmatchedCards(hasUnmatchedCards));
      dispatch(actionSetHasOpenLaborTickets(hasOpenLaborTickets));
      dispatch(actionSetOpenLaborTicketRefs(openLaborTicketRefs));
      dispatch(actionSetHasOpenSetupLaborTickets(hasOpenSetupLaborTickets));
      dispatch(actionSetProgramEvents(events));
    }
  }, [
    enabled,
    cardCount,
    hasCards,
    hasUnmatchedCards,
    hasOpenLaborTickets,
    openLaborTicketRefs,
    hasOpenSetupLaborTickets,
    events,
    loading,
    dispatch,
  ]);

  return { refetch: refetchEvents, events, eventsLoading: showEventsLoading };
};
